import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type Theme = 'light' | 'dark';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly THEME_KEY = 'app-theme';
  private currentTheme = new BehaviorSubject<Theme>('light');

  constructor() {
    this.initializeTheme();
  }

  get theme$(): Observable<Theme> {
    return this.currentTheme.asObservable();
  }

  get currentThemeValue(): Theme {
    return this.currentTheme.value;
  }

  setTheme(theme: Theme): void {
    this.currentTheme.next(theme);
    this.saveTheme(theme);
    this.applyTheme(theme);
  }

  toggleTheme(): void {
    const newTheme = this.currentThemeValue === 'light' ? 'dark' : 'light';
    this.setTheme(newTheme);
  }

  private initializeTheme(): void {
    const savedTheme = this.getSavedTheme();
    const systemTheme = this.getSystemTheme();
    const initialTheme = savedTheme || systemTheme;

    this.setTheme(initialTheme);
    this.setupSystemThemeListener();
  }

  private getSavedTheme(): Theme | null {
    if (typeof localStorage === 'undefined') return null;

    try {
      const theme = localStorage.getItem(this.THEME_KEY) as Theme;
      return theme && ['light', 'dark'].includes(theme) ? theme : null;
    } catch {
      return null;
    }
  }

  private getSystemTheme(): Theme {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private setupSystemThemeListener(): void {
    if (typeof window === 'undefined') return;

    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      // Solo cambiar si no hay preferencia guardada
      if (!this.getSavedTheme()) {
        this.setTheme(e.matches ? 'dark' : 'light');
      }
    });
  }

  private saveTheme(theme: Theme): void {
    if (typeof localStorage === 'undefined') return;

    try {
      localStorage.setItem(this.THEME_KEY, theme);
    } catch (error) {
      console.warn('No se pudo guardar el tema:', error);
    }
  }

  private applyTheme(theme: Theme): void {
    if (typeof document === 'undefined') return;

    const html = document.documentElement;

    // Aplicar o quitar la clase 'dark' para Tailwind y PrimeNG
    if (theme === 'dark') {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }

    this.updateMetaThemeColor(theme);
  }

  private updateMetaThemeColor(theme: Theme): void {
    if (typeof document === 'undefined') return;

    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      const color = theme === 'dark' ? '#0f172a' : '#ffffff';
      metaThemeColor.setAttribute('content', color);
    }
  }
}
